import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { get, post } from '@/lib/api-client';

// ---------------------------------------------------------------------------
// Interfaces (mirror backend types)
// ---------------------------------------------------------------------------

export interface CatalogStats {
  total: number;
  lastUpdated: string | null;
  byRegion: Record<string, number>;
  byMarket: Record<string, number>;
  byType: Record<string, number>;
  digitalCount: number;
  nonDigitalCount: number;
}

export interface CatalogRowError {
  row: number;
  field: string;
  message: string;
}

export interface PreviewResult {
  previewId: string;
  validCount: number;
  errorCount: number;
  rowErrors: CatalogRowError[];
  totalRowsProcessed: number;
  sampleRows: Record<string, string | null>[];
}

export interface ImportResult {
  recordsLoaded: number;
  rowErrors: CatalogRowError[];
  totalRowsProcessed: number;
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/**
 * Fetch current product catalog statistics.
 */
export function useProductCatalogStats() {
  return useQuery({
    queryKey: ['productCatalogStats'],
    queryFn: async () => {
      const response = await get<{
        success: boolean;
        data: CatalogStats;
      }>('/admin/product-catalog/stats');
      return response.data;
    },
  });
}

/**
 * Upload a CSV file for preview (validation without committing).
 */
export function usePreviewCatalogUpload() {
  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);

      const response = await post<
        { success: boolean; data: PreviewResult },
        FormData
      >('/admin/product-catalog/preview', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      return response.data;
    },
  });
}

/**
 * Confirm a previously previewed catalog import.
 * Invalidates the stats query on success so the UI refreshes.
 */
export function useConfirmCatalogImport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (previewId: string) => {
      const response = await post<
        { success: boolean; data: ImportResult },
        { previewId: string }
      >('/admin/product-catalog/confirm', { previewId });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['productCatalogStats'] });
    },
  });
}
